/**
 * Represents a customizable modal component.
 *
 * @component
 * @description This component renders a button that, when clicked, opens a modal dialog. The modal can be customized with various
 * properties such as size, title, and styles. It also logs user activity when the modal is opened.
 *
 * @param {Object} props - The component props.
 * @param {string} props.buttonLabel - The label text for the button that triggers the modal.
 * @param {React.ReactNode} props.content - The content to be displayed inside the modal body.
 * @param {string} props.id - The ID of the button element.
 * @param {string} props.modalId - The ID of the modal element.
 * @param {string} [props._className="modalBox"] - Custom CSS class for the modal.
 * @param {string} [props.buttonClass="btn btn-primary btn-sm"] - Custom CSS class for the button.
 * @param {string} [props.title=""] - Title to be displayed in the modal header.
 * @param {string} [props.header=""] - Header text for the modal.
 * @param {Object} [props.style={}] - Inline styles for the button.
 * @param {Object} [props.overflowStyles={}] - Inline styles for overflow handling in the modal.
 * @param {boolean} [props.disabled=false] - Whether the button should be disabled.
 * @param {boolean} [props.backdrop=false] - Whether to show the modal backdrop.
 * @param {string} [props.screenName=""] - Screen name for activity logging.
 * @param {string} [props.moduleName=""] - Module name for activity logging.
 * @param {string} [props.activityName=""] - Activity name for activity logging.
 * @param {boolean} [props.closeButtonNeeded=true] - Whether the modal should have a close button.
 * @param {string} [props.dialogClassName="modal-dialog"] - Custom CSS class for the modal dialog.
 * @param {string} [props.buttonTitle=""] - Tooltip text for the button.
 * @param {string} [props.procurement_id=""] - Procurement ID for activity logging.
 * @param {function} [props.reload] - Function to be called when the modal is closed.
 * @param {string} [props.size] - Size of the modal (e.g., 'sm', 'lg', 'xl').
 * @param {function} [props.onModalButtonClick=(() => {return null})] - Function to be called when the button is clicked.
 * @returns {React.ReactElement} A button element that triggers a modal, and the modal itself.
 */
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Activity,
  currentRequestTimeStamp,
  getProcurementID,
  isMobile,
} from "../Constants";
import "./modal.css";

const ModalBox = ({
  isIcon = false,
  buttonLabel,
  content,
  id,
  modalId,
  _className = "modalBox",
  buttonClass = "btn btn-primary btn-sm",
  title = "",
  header = "",
  style = {},
  overflowStyles = {},
  disabled = false,
  backdrop = false,
  screenName = "",
  moduleName = "",
  activityName = "",
  closeButtonNeeded = true,
  dialogClassName = "modal-dialog",
  buttonTitle = "",
  procurement_id = "",
  reload,
  size,
  onModalButtonClick = () => {
    return null;
  },
  onCloseModal = () => {
    return null;
  },
}) => {
  const [lgShow, setLgShow] = useState(false);

  /**
   * Closes the modal and optionally reloads the page.
   *
   * @param {boolean} reload - Whether to reload the page when closing the modal.
   */
  const closeModal = (reload) => {
    if (reload) {
      window.location.reload();
      setLgShow(false);
    } else {
      setLgShow(false);
    }
  };

  /**
   * Logs user activity when the modal is opened.
   */
  const logActivity = () => {
    setLgShow(!lgShow);
    setTimeout(() => {
      if (lgShow === false) {
        Activity([
          {
            screen: screenName,
            module: moduleName,
            user_activity: activityName,
            timestamp: currentRequestTimeStamp(),
            procurement_id: getProcurementID()
              ? getProcurementID()
              : procurement_id,
            email_status: "pending",
          },
        ]);
      }
    }, 2000);
  };

  return (
    <>
      <button
        title={buttonTitle}
        type="button"
        disabled={disabled}
        className={`${buttonClass} modal_button_popup`}
        id={id}
        style={style}
        onClick={() => {
          onModalButtonClick();
          logActivity();
        }}
      >
        {buttonLabel}
      </button>
      <Modal
        size={size ? size : "lg"}
        id={modalId}
        show={lgShow}
        onHide={() => closeModal(false)}
        className={_className}
        dialogClassName={dialogClassName}
        animation={false}
        style={overflowStyles}
        backdrop={backdrop}
        keyboard={false}
      >
        <Modal.Header>
          <div
            style={{
              width: "100%",
              display: "inline-flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {header !== "" ? (
              <h3
                style={{ width: "95%", display: isMobile() ? "none" : "block" }}
              >
                {header}
              </h3>
            ) : (
              title
            )}
            {closeButtonNeeded ? (
              <button
                id="modal_close_button"
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if(moduleName === "Add Lanes" && onCloseModal()){
                    closeModal(reload);
                  }else if(moduleName !== "Add Lanes"){
                    onCloseModal()
                    closeModal(reload)
                  }
                }}
              >
                <i className="fa fa-close"></i>
              </button>
            ) : null}
          </div>
        </Modal.Header>
        <Modal.Body>{content}</Modal.Body>
      </Modal>
    </>
  );
};
export default ModalBox;
